<template>
  <base-register classes="candidate-register">
    <div slot="left-bar">
      <svg-icon name="candidate-approved" width="120" height="120"></svg-icon>
      <facts-slider-candidate></facts-slider-candidate>
    </div>

    <template slot="main-content">
      <h3 class="heading-3 header-marker">{{ labels.title }}</h3>

      <highlighted-text-block v-if="isApplyOnJob">
        <span v-translate class="paragraph">Sign up to add your CV, track your applications, earn referral bonus, and more. We will contact you if you are a fit for this position with the information you provided.</span>
      </highlighted-text-block>
      <p v-else class="paragraph-l">{{ labels.subtitle }}</p>

      <template v-if="showSocialSignUpButtons">
        <div v-if="registerFormNonFieldErrors" class="errors">{{ registerFormNonFieldErrors }}</div>
        <social-sign-up
          :loading="registerFormLoading"
          @start-sign-up="handleSocialSignUp()"
          @cancel-sign-up="clearTargetRoute()">
        </social-sign-up>
      </template>

      <info-text-block v-if="referrerProfileState && !isReferJobLink">
        <div class="paragraph-s inner" v-html="labels.referrerInfoI18N"></div>
      </info-text-block>
      <info-text-block v-if="isActionRefer && maxPotentialBonusState">
        <div class="paragraph-s inner" v-html="labels.subheaderInfo"></div>
      </info-text-block>

      <register-form
        ref="form"
        :fields="visibleFields"
        :error="registerFormError"
        :loading="registerFormLoading"
        :disabled="disabled"
        :handleChangeField="handleChangeField"
        :handleRegister="handleRegister"
        class="register-form"
        @sign-up-click="trackSignUpClick">
        <cv-file-upload
          v-if="showResumeField"
          slot="after-fields-form"
          :files="resumeState.file"
          :loading="resumeState.loading"
          :error="resumeState.error"
          @on-file-attach="handleUploadResume"
          @on-file-remove="handleRemoveResume">
        </cv-file-upload>
      </register-form>

      <div class="login-section paragraph">
        <span v-translate>Already have an account?</span>
        <alternative-registration-login-type-link
          :linkLabel="labels.login"
          routeName="login-candidate"
          @click="loginClick"
        ></alternative-registration-login-type-link>
      </div>

      <template>
        <info-text-block v-if="!isActionRefer">
          <div v-if="referrerProfileState" class="paragraph-s inner" v-html="labels.referrerDetailedI18N"></div>
          <div class="paragraph-s inner">
            {{ labels.infoIsSafe }}
            {{ labels.ourText }}
            <a v-translate :href="privacyLink" target="_blank" class="primary-link" @click="trackPrivacyPolicyClick()">Privacy Policy</a>
            {{ labels.explainsText }}
          </div>
        </info-text-block>
      </template>

      <alternative-register-login-option
        type="employer"
        @sign-up="trackSignUpAsEmployerClick"
      ></alternative-register-login-option>
    </template>
  </base-register>
</template>

<script>

import 'top20common/distribution/icons/candidate-approved';

import {isEmpty} from 'lodash';
import CvFileUpload from 'top20common/components/file-upload/CvFileUpload';
import HighlightedTextBlock from 'top20common/components/text-blocks/HighlightedTextBlock';
import InfoTextBlock from 'top20common/components/text-blocks/InfoTextBlock';
import currencies from 'top20common/distribution/services/currencies';
import formatter from 'top20common/distribution/services/formatter';
import redirects from 'top20common/distribution/services/redirects';
import storage from 'top20common/distribution/services/storage';
import commonUtils from 'top20common/distribution/utils';
import {mapActions, mapGetters} from 'vuex';

import AlternativeRegisterLoginOption from '@/common/AlternativeRegisterLoginOption';
import AlternativeRegistrationLoginTypeLink from '@/common/AlternativeRegistrationLoginTypeLink';
import FactsSliderCandidate from '@/common/FactsSliderCandidate';
import socialsMixin from '@/mixins/socialsMixin';
import BaseRegister from '@/pages/register/components/BaseRegister';
import RegisterForm from '@/pages/register/components/RegisterForm';
import SocialSignUp from '@/pages/register/components/SocialSignUp';
import utils from '@/services/utils';
import {REGISTER_FINISH_FORM} from '@/utils/constants/storage';

export default {
  mixins: [socialsMixin],
  mounted() {
    document.body.className = 'register-page';
    this.$store.commit('clearRegisterFormError');
  },
  beforeDestroy() {
    this.$store.commit('clearCandidateRegisterForm');
    document.body.className = '';
  },
  created() {
    this.$store.commit('clearCandidateRegisterForm');
    this.$store.commit('clearRegisterFormError');
    this.prefillRegisterForm();
    this.trackPreApplicationCreated();
    this.specifyNextRoute();
    this.handleRouteQuery();
    this.clearRouteQueryFromSocialKeys();
  },
  data() {
    return {
      nextRoute: '',
      employer: 'employer',
      showResumeField: true
    };
  },
  computed: {
    ...mapGetters([
      'featureSwitchesState',
      'resumeState',
      'referrerProfileState',
      'minPotentialBonusState',
      'maxPotentialBonusState',
      'potentialBonusCurrencyState',
      'jobsContainerIdState',
      'recommendedJobsContainerIdState',
      'candidateRegisterForm',
      'registerFormError',
      'registerFormNonFieldErrors',
      'registerFormLoading',
      'userDetailsState',
      'jobDetailsState',
      'previousRouteNameState'
    ]),
    langPrefix() {
      return this.$language.isChinese() ? '/cn' : '';
    },
    privacyLink() {
      return `${this.langPrefix}/privacy-policy-candidate/`;
    },
    isActionRefer() {
      return this.$route.query.action === 'refer';
    },
    isReferJobLink() {
      return this.isActionRefer && !isEmpty(this.jobDetailsState);
    },
    isSourceTop20Tech() {
      return this.$route.query.source === 'top20tech';
    },
    referralCode() {
      return this.$route.query.referral_code;
    },
    jobCode() {
      return this.$route.query.job_code;
    },
    disabled() {
      return (
        this.resumeState.loading ||
        !this.candidateRegisterForm.phone ||
        !commonUtils.isValidPhone(this.candidateRegisterForm.phone) ||
        !this.candidateRegisterForm.full_name ||
        !utils.isValidEmail(this.candidateRegisterForm.email) ||
        !this.isPasswordValid ||
        !this.phoneVerified
      );
    },
    phoneVerified() {
      if (this.featureSwitchesState.CANDIDATE_SIGN_UP_WITH_PHONE) {
        return this.candidateRegisterForm.verification_code;
      }

      return true;
    },
    isPasswordValid() {
      return this.candidateRegisterForm.password1;
    },
    isApplyOnJob() {
      return this.jobCode && !this.isActionRefer;
    },
    showSocialSignUpButtons() {
      return (
        (this.featureSwitchesState.CANDIDATE_LINKEDIN_SIGNUP || this.featureSwitchesState.CANDIDATE_WECHAT_SIGNUP) &&
        this.$context.isO2O
      );
    },
    fields() {
      return [
        {
          label: this.$gettext('Full Name'),
          value: 'full_name',
          initialValue: this.candidateRegisterForm.full_name,
          type: 'text'
        },
        {
          label: this.$gettext('Email'),
          value: 'email',
          initialValue: this.candidateRegisterForm.email,
          type: 'email'
        },
        this.phoneField,
        {
          label: this.$gettext('Verification code'),
          value: 'verification_code',
          initialValue: this.candidateRegisterForm.verification_code,
          type: 'text',
          phoneNumber: this.candidateRegisterForm.phone,
          isPhoneVerification: true,
          isHidden: !this.featureSwitchesState.CANDIDATE_SIGN_UP_WITH_PHONE
        },
        {
          label: this.$gettext('Password'),
          value: 'password1',
          initialValue: this.candidateRegisterForm.password1,
          isPassword: true,
          type: 'text'
        }
      ];
    },
    phoneWithCountryCode() {
      return this.featureSwitchesState.CANDIDATE_SIGN_UP_WITH_PHONE ? {label: '', isPhoneWithCountryField: true} : {};
    },
    phoneField() {
      return {
        label: this.$gettext('Phone'),
        value: 'phone',
        initialValue: this.candidateRegisterForm.phone,
        type: 'text',
        ...this.phoneWithCountryCode
      };
    },
    visibleFields() {
      return this.fields.filter(field => !field.isHidden);
    },
    titleLabel() {
      if (this.isApplyOnJob) {
        return this.$gettext('Sign up to submit your application');
      }
      return this.$gettext('Candidate sign up');
    },
    labels() {
      const value = currencies.getSimplifiedMoneyRange(
        this.minPotentialBonusState,
        this.maxPotentialBonusState,
        this.potentialBonusCurrencyState
      );

      const firstName = this.referrerProfileState ? this.referrerProfileState.first_name : '';
      const lastName = this.referrerProfileState ? this.referrerProfileState.last_name : '';
      let referrerDetailed = this.$gettext(
        '<strong>* %{firstName} %{lastName} </strong> has referred you to us. If you find a job through %{platformName}, you and %{firstName} both make <strong>%{value}</strong>'
      );
      let referrerDetailedAnon = this.$gettext(
        '<strong>* Someone </strong> has referred you to us. If you find a job through %{platformName}, you and your referrer both make <strong>%{value}</strong>'
      );
      let referrerInfoAnon = this.$gettext(
        '<strong>Someone </strong> referred you 🚀 Apply now and earn <strong>%{value} *</strong> if you get hired'
      );
      let referrerInfo = this.$gettext(
        '<strong>%{firstName} %{lastName}</strong> referred you 🚀 Apply now and earn <strong>%{value} *</strong> if you get hired'
      );

      const platformName = this.$context.title;

      let referrerInfoI18N =
        lastName || firstName
          ? this.$gettextInterpolate(referrerInfo, {lastName, firstName, value})
          : this.$gettextInterpolate(referrerInfoAnon, {value});
      let referrerDetailedI18N =
        lastName || firstName
          ? this.$gettextInterpolate(referrerDetailed, {lastName, firstName, value, platformName})
          : this.$gettextInterpolate(referrerDetailedAnon, {value, platformName});

      let subheaderInfo = this.$gettext('Create an account and share referral link with your friends');

      return {
        title: this.titleLabel,
        subtitle: this.$gettext('Meet your next dream job!'),
        subheaderInfo: this.$gettextInterpolate(subheaderInfo, {value}),
        referrerDetailedI18N: referrerDetailedI18N,
        referrerInfoI18N: referrerInfoI18N,
        infoIsSafe: this.$gettext(
          'We keep your information private and invisible to your current employer. Only companies we talk to you about first can see your details.'
        ),
        ourText: this.$gettext('Our'),
        explainsText: this.$gettext('explains in detail how we use and protect your information.'),
        login: this.$gettext('Login')
      };
    }
  },
  methods: {
    ...mapActions(['getReferrerProfileAction', 'getJobAction']),
    loginClick() {
      this.$mam.trackLoginFromRegisterClicked();
      this.clearRegisterForm();
      document.body.className = '';
      redirects.removeTargetRoute();
    },
    trackSignUpClick(isBtnDisabled) {
      if (this.candidateRegisterForm.full_name && this.candidateRegisterForm.full_name.includes('e2e')) {
        return;
      }
      this.$mam.trackRegularSignUpClicked(isBtnDisabled);
    },
    trackPrivacyPolicyClick() {
      this.$mam.trackPrivacyPolicyClicked();
    },
    trackSignUpAsEmployerClick() {
      this.$mam.trackSignUpClicked(this.employer);
    },
    handleRegister() {
      if (!this.disabled) {
        if (!isEmpty(this.userDetailsState)) {
          this.$mam.reset();
        }

        const additionalRegistrationData = {password2: this.candidateRegisterForm.password1};
        const registerForm = {...this.candidateRegisterForm, ...additionalRegistrationData};

        this.$store
          .dispatch('candidateRegisterAction', {registerForm, vue: this})
          .then(response => {
            this.clearRegisterForm();
            this.setTargetRoute();
            redirects.redirectToTargetRouteAfterLogin();
          })
          .catch(error => {
            this.$mam.trackSignUpError(error.response);
          });
      }
    },
    handleSocialSignUp() {
      let keysToStore = ['phone', 'email'];
      let data = {};
      keysToStore.forEach(key => {
        if (this.candidateRegisterForm[key]) {
          data[key] = this.candidateRegisterForm[key];
        }
      });

      if (!isEmpty(data)) {
        storage.setJsonItem(REGISTER_FINISH_FORM, data);
      }

      this.setTargetRoute();
    },
    handleUploadResume(file) {
      if (this.resumeState.file !== file) {
        this.$store
          .dispatch('parseResumeAction', {file})
          .then(({uid, ...data}) => {
            this.$store.commit('mutateCandidateRegisterForm', this.getNotEmpty(data));
            this.setFocusInFirstEmptyField();
          })
          .catch(() => this.parseError());
      }
    },
    parseError() {
      this.$toast.open({
        message: this.$gettext("Our system couldn't read your CV file. Make sure you upload a text file."),
        type: 'is-danger'
      });
    },
    handleRemoveResume() {
      this.$store.commit('mutateResumeState', {file: null, info: null});
    },
    getNotEmpty(data) {
      let info = {...data};
      Object.keys(info).forEach(key => (info[key] === null || !info[key]) && delete info[key]);

      return info;
    },
    handleRouteQuery() {
      this.$store.commit('mutateCandidateRegisterForm', {analytics: this.$route.query});
      this.$store.commit('mutateRegisterFormError', {
        non_field_errors: this.linkedInErrors[this.$route.query.error] || this.$route.query.error_description
      });
      this.setExternalCandidateId();
      this.getReferrerProfile();
      this.getJobByCode();
      this.getReferralPotentialBonus();
    },
    getReferrerProfile() {
      if (this.referralCode) {
        this.getReferrerProfileAction(this.referralCode).then(() => {
          this.$store.commit('mutateCandidateRegisterForm', {referral_code: this.referralCode});
        });
      }
    },
    getJobByCode() {
      if (this.jobCode) {
        this.getJobAction(this.jobCode)
          .then(() => {
            if (this.isActionRefer) {
              this.specifyNextRoute();
            } else {
              this.$store.commit('mutatePreviousAnchorState', this.jobsContainerIdState);
              this.$store.commit('mutateCandidateRegisterForm', {job_code: this.jobCode});
            }
          })
          .catch(() => {});
      }
    },
    getReferralPotentialBonus() {
      if (this.isActionRefer || this.referralCode) {
        this.$store.dispatch('getReferralPotentialBonusAction', {
          jobCode: this.jobCode,
          action: this.$route.query.action
        });
      }
    },
    setExternalCandidateId() {
      if (this.$route.query.candidate) {
        this.$store.commit('mutateCandidateRegisterForm', {external_id: this.$route.query.candidate});
      }
    },
    handleChangeField(input) {
      const formOption = {
        [input.target.name]: input.target.type === 'checkbox' ? input.target.checked : input.target.value
      };
      this.$store.commit('mutateCandidateRegisterForm', formOption);
    },
    prefillRegisterForm() {
      let rd = storage.getJsonItem('register-data');
      if (rd) {
        let {resume, ...registerData} = rd;
        if (resume) {
          this.showResumeField = false; // hide resume field to avoid user changing resume file after pre-application
        }
        if (
          registerData.phone &&
          this.featureSwitchesState.CANDIDATE_SIGN_UP_WITH_PHONE &&
          !registerData.phone.startsWith('+')
        ) {
          registerData.phone = `+${registerData.phone}`;
        }
        this.$store.commit('mutateCandidateRegisterForm', registerData);
        this.$store.commit('mutateResumeState', {info: resume});
        localStorage.removeItem('register-data');
      }
    },
    trackPreApplicationCreated() {
      let data = storage.getJsonItem('job-apply-data');
      if (data) {
        this.$mam.trackSubmitApplicationClicked(data);
        localStorage.removeItem('job-apply-data');
      }
    },
    setFocusInFirstEmptyField() {
      let emptyFields = this.fields.filter(field => !this.candidateRegisterForm[field.value]);
      if (emptyFields.length) {
        let emptyFieldName = emptyFields[0].value;

        if (this.$refs.form.$refs.register.$refs.hasOwnProperty(emptyFieldName)) {
          this.$refs.form.$refs.register.$refs[emptyFieldName][0].$refs.input.focus();
        }
      }
    },
    clearRouteQueryFromSocialKeys() {
      this.$router.push({
        name: 'register-candidate',
        query: this.queryWithoutSocialKeys
      });
    },
    clearRegisterForm() {
      this.$store.commit('clearCandidateRegisterForm');
      this.$store.commit('clearRegisterFormError');
    },
    specifyNextRoute() {
      this.nextRoute = this.getNextRoute();
    },
    setTargetRoute() {
      if (this.nextRoute) {
        redirects.saveTargetRouteBeforeLogin('/' + this.nextRoute);
      }
    },
    clearTargetRoute() {
      redirects.removeTargetRoute();
    },
    getNextRoute() {
      if (this.isActionRefer) {
        if (this.isReferJobLink) {
          let jobSlug = formatter.slugify(this.jobDetailsState.title);
          return `${redirects.CANDIDATE_JOBS_URL}/${this.jobCode}/${jobSlug}`;
        }

        return redirects.CANDIDATE_DASHBOARD_URL;
      } else {
        return redirects.CANDIDATE_QUESTIONNAIRE_URL;
      }
    }
  },
  components: {
    BaseRegister,
    RegisterForm,
    SocialSignUp,
    FactsSliderCandidate,
    InfoTextBlock,
    AlternativeRegistrationLoginTypeLink,
    AlternativeRegisterLoginOption,
    CvFileUpload,
    HighlightedTextBlock
  }
};
</script>

<style lang="scss" scoped>
.facts-slider-candidate {
  margin-top: 20px;
}
.register-form {
  margin-top: 35px;
}
.errors {
  color: var(--error);
  margin-bottom: 5px;
  margin-top: 30px;
}

.login-section {
  margin-top: 25px;
}

/deep/ .info-text-block {
  margin-top: 20px;
  .inner:not(:last-child) {
    padding-bottom: 20px;
  }
}

/deep/ .form-fields > .field:not(:last-child) {
  margin-bottom: 20px;
}

/deep/ .paragraph-highlighted {
  margin-top: 15px;
}
</style>
